import { supabase } from "@/integrations/supabase/client"
import type {
  CobrancaItem,
  ItemProposta,
  ModalidadeProposta,
  Proposta,
  StatusProposta,
  TipoModalidade,
} from "@/types/comercial"

// ── Formatação ───────────────────────────────────────────────────────────────

const brl = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" })

export function fmtCurrency(v: number) {
  return brl.format(Number.isFinite(v) ? v : 0)
}

export function fmtDate(d?: string) {
  if (!d) return "—"
  const date = d.length === 10 ? new Date(`${d}T00:00:00`) : new Date(d)
  return date.toLocaleDateString("pt-BR")
}

export function uid(): string {
  return crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36)
}

export function publicUrl(p: Pick<Proposta, "id">) {
  return `${window.location.origin}/p/${p.id}`
}

// ── Status ───────────────────────────────────────────────────────────────────

export const STATUS_LABELS: Record<StatusProposta, string> = {
  rascunho: "Rascunho",
  enviada: "Enviada",
  aceita: "Aceita",
  recusada: "Recusada",
  vencida: "Vencida",
}

// ── Itens ────────────────────────────────────────────────────────────────────

const MENSAL_HINTS = ["mensal", "licença", "licenca", "mensalidade", "recorrente", "assinatura", "/mês", "/mes"]

/** Cobrança do item; itens antigos (sem o campo) são inferidos pela descrição. */
export function cobrancaDoItem(item: ItemProposta): CobrancaItem {
  if (item.cobranca) return item.cobranca
  const d = item.descricao.toLowerCase()
  return MENSAL_HINTS.some((hint) => d.includes(hint)) ? "mensal" : "unica"
}

export function totaisDosItens(itens: ItemProposta[]) {
  return itens.reduce(
    (acc, item) => {
      acc[cobrancaDoItem(item)] += item.valorTotal
      return acc
    },
    { unica: 0, mensal: 0 } as Record<CobrancaItem, number>,
  )
}

// ── Modalidades ──────────────────────────────────────────────────────────────

export const MODALIDADE_PRESETS: Record<TipoModalidade, Omit<ModalidadeProposta, "id" | "valorUnico" | "valorMensal">> = {
  compra: {
    tipo: "compra",
    nome: "Compra do sistema",
    descricao: "Licença definitiva. O sistema passa a ser seu após o pagamento.",
    parcelas: 1,
    fidelidadeMeses: 0,
    inclusos: ["Licença de uso definitiva", "Implantação e treinamento", "90 dias de suporte incluso"],
  },
  locacao: {
    tipo: "locacao",
    nome: "Locação mensal",
    descricao: "Sem investimento alto na entrada. Atualizações e suporte inclusos na mensalidade.",
    parcelas: 1,
    fidelidadeMeses: 12,
    inclusos: ["Atualizações contínuas", "Suporte técnico ilimitado", "Hospedagem e backup em nuvem"],
  },
  personalizada: {
    tipo: "personalizada",
    nome: "Condição personalizada",
    descricao: "",
    parcelas: 1,
    fidelidadeMeses: 0,
    inclusos: [],
  },
}

export function novaModalidade(tipo: TipoModalidade, itens: ItemProposta[] = []): ModalidadeProposta {
  const totais = totaisDosItens(itens)
  const preset = MODALIDADE_PRESETS[tipo]
  return {
    ...preset,
    inclusos: [...preset.inclusos],
    id: uid(),
    valorUnico: tipo === "locacao" ? 0 : totais.unica,
    valorMensal: tipo === "compra" ? 0 : totais.mensal,
  }
}

/** Total do contrato considerando a fidelidade (ou 12 meses quando não há). */
export function custoNoPeriodo(m: ModalidadeProposta, meses = m.fidelidadeMeses || 12) {
  return m.valorUnico + m.valorMensal * meses
}

export function resumoModalidade(m: ModalidadeProposta) {
  const partes: string[] = []
  if (m.valorUnico > 0) {
    partes.push(m.parcelas > 1 ? `${m.parcelas}x ${fmtCurrency(m.valorUnico / m.parcelas)}` : fmtCurrency(m.valorUnico))
  }
  if (m.valorMensal > 0) partes.push(`${fmtCurrency(m.valorMensal)}/mês`)
  return partes.join(" + ") || "Sem custo"
}

// ── Banco ────────────────────────────────────────────────────────────────────

/*
 * As colunas `modalidades` e `modalidade_escolhida` chegam pela migration
 * 20261014120000_propostas_modalidades.sql. Enquanto ela não for aplicada, o
 * save cai num fallback sem essas colunas e guarda as modalidades no navegador.
 */
const LOCAL_MODALIDADES_KEY = "itacondo_proposta_modalidades"

type ModalidadesLocais = Record<string, { modalidades: ModalidadeProposta[]; escolhida?: string }>

function lerModalidadesLocais(): ModalidadesLocais {
  try {
    return JSON.parse(localStorage.getItem(LOCAL_MODALIDADES_KEY) || "{}")
  } catch {
    return {}
  }
}

function gravarModalidadesLocais(p: Proposta) {
  try {
    const all = lerModalidadesLocais()
    all[p.id] = { modalidades: p.modalidades ?? [], escolhida: p.modalidadeEscolhida }
    localStorage.setItem(LOCAL_MODALIDADES_KEY, JSON.stringify(all))
  } catch {
    /* armazenamento indisponível: segue só com o banco */
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function propostaFromRow(row: any): Proposta {
  const local = lerModalidadesLocais()[row.id]
  const modalidades: ModalidadeProposta[] =
    Array.isArray(row.modalidades) && row.modalidades.length > 0 ? row.modalidades : local?.modalidades ?? []
  return {
    id: row.id,
    titulo: row.titulo,
    clienteNome: row.cliente_nome,
    clienteEmail: row.cliente_email ?? "",
    clienteTelefone: row.cliente_telefone || undefined,
    clienteEmpresa: row.cliente_empresa || undefined,
    status: row.status as StatusProposta,
    itens: (row.itens as ItemProposta[]) ?? [],
    valorTotal: Number(row.valor_total ?? 0),
    desconto: Number(row.desconto ?? 0),
    valorFinal: Number(row.valor_final ?? 0),
    validadeAte: row.validade_ate,
    escopo: row.escopo || undefined,
    observacoes: row.observacoes || undefined,
    termos: row.termos || undefined,
    token: row.token,
    linkPublico: publicUrl(row),
    historico: row.historico ?? [],
    aceitePor: row.aceite_por || undefined,
    aceiteEm: row.aceite_em || undefined,
    recusaPor: row.recusa_por || undefined,
    recusaEm: row.recusa_em || undefined,
    recusaMotivo: row.recusa_motivo || undefined,
    senha: row.senha || undefined,
    modalidades,
    modalidadeEscolhida: row.modalidade_escolhida || local?.escolhida || undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

function propostaToRow(p: Proposta) {
  return {
    id: p.id,
    titulo: p.titulo,
    cliente_nome: p.clienteNome,
    cliente_email: p.clienteEmail,
    cliente_telefone: p.clienteTelefone || null,
    cliente_empresa: p.clienteEmpresa || null,
    status: p.status,
    itens: p.itens as never,
    valor_total: p.valorTotal,
    desconto: p.desconto,
    valor_final: p.valorFinal,
    validade_ate: p.validadeAte,
    escopo: p.escopo || null,
    observacoes: p.observacoes || null,
    termos: p.termos || null,
    token: p.token ?? uid(),
    historico: (p.historico ?? []) as never,
    aceite_por: p.aceitePor || null,
    aceite_em: p.aceiteEm || null,
    recusa_por: p.recusaPor || null,
    recusa_em: p.recusaEm || null,
    recusa_motivo: p.recusaMotivo || null,
    senha: p.senha || null,
    updated_at: new Date().toISOString(),
  }
}

function colunaInexistente(error: { code?: string; message?: string }) {
  return error.code === "PGRST204" || /modalidade/i.test(error.message ?? "")
}

/** Upsert da proposta. Retorna `local` quando as modalidades ficaram só no navegador. */
export async function salvarProposta(p: Proposta): Promise<{ modalidadesNoBanco: boolean }> {
  gravarModalidadesLocais(p)
  const row = propostaToRow(p)
  const { error } = await supabase
    .from("propostas")
    .upsert({ ...row, modalidades: (p.modalidades ?? []) as never, modalidade_escolhida: p.modalidadeEscolhida ?? null } as never)
  if (!error) return { modalidadesNoBanco: true }
  if (!colunaInexistente(error)) throw error

  const retry = await supabase.from("propostas").upsert(row)
  if (retry.error) throw retry.error
  return { modalidadesNoBanco: false }
}

// ── Link público (/p/:id) ────────────────────────────────────────────────────

/*
 * O anon não lê nem altera a tabela propostas: a página pública usa as RPCs
 * de 20261015100000_public_links_rpcs.sql, que conferem a senha no servidor e
 * nunca devolvem a coluna senha.
 */
export type AcessoPublico = "ok" | "senha_requerida" | "senha_incorreta"

export interface PropostaPublicaResult {
  acesso: AcessoPublico
  protegida: boolean
  proposta?: Proposta
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function publicaFromRpc(data: any): PropostaPublicaResult | null {
  if (!data) return null
  return {
    acesso: data.acesso,
    protegida: !!data.protegida,
    proposta: data.proposta ? propostaFromRow(data.proposta) : undefined,
  }
}

/** Busca a proposta pelo id (ou token legado). `null` quando não existe. */
export async function carregarPropostaPublica(id: string, senha?: string): Promise<PropostaPublicaResult | null> {
  const { data, error } = await supabase.rpc("get_public_proposta", { p_id: id, p_senha: senha ?? null })
  if (error) throw error
  return publicaFromRpc(data)
}

export type AcaoPublica = "aceitar" | "recusar" | "revisao"

/** Registra a resposta do cliente e devolve a proposta atualizada. */
export async function responderPropostaPublica(
  id: string,
  senha: string | undefined,
  acao: AcaoPublica,
  opts: { modalidadeId?: string; motivo?: string } = {},
): Promise<Proposta | undefined> {
  const { data, error } = await supabase.rpc("responder_proposta", {
    p_id: id,
    p_senha: senha ?? null,
    p_acao: acao,
    p_modalidade_id: opts.modalidadeId ?? null,
    p_motivo: opts.motivo ?? null,
  })
  if (error) throw error
  return publicaFromRpc(data)?.proposta
}
